import type { FC, ReactElement } from 'react';
import { useCallback } from 'react';

import { useAudioTrigger } from '../../../../../controllers/audioController/hooks';
import { chatCommunicationController } from '../../../../../controllers/chatCommunication/ChatCommunicationControllerConnector';
import { useCoreDispatch } from '../../../../../store/CoreStore';
import { useChatSlice } from '../../../../../store/slices/chat/selectors';
import { chatActions } from '../../../../../store/slices/chat/slice';
import type { PrivateMessageType } from '../../../../../store/slices/chat/types';
import { useConfigSlice } from '../../../../../store/slices/config/selectors';
import { useIsSmallerThanWide } from '../../../../../styles/hooks/media';
import { generateMessage } from '../../../../../utils/chat';

import { PrivateChatOptions } from './PrivateChatOptions';

export const PrivateChatOptionsConnected: FC = (): ReactElement => {
  const userId = useConfigSlice.playerConfig()?.userId ?? '';
  const casinoId = useConfigSlice.session()?.casinoId ?? '';
  const tableId = useConfigSlice.session()?.tableId ?? '';

  const audioTrigger = useAudioTrigger();
  const dispatch = useCoreDispatch();
  const isMobile = useIsSmallerThanWide();

  const isSpectator = useConfigSlice.isSpectatorUser();
  const isExpanded = useChatSlice.getIsPrivateOptionsExpanded();
  const privateChatOptions: PrivateMessageType[] = useChatSlice.getPrivateChatOptions();

  const onToggle = (): void => {
    dispatch(chatActions.setIsPrivateOptionsExpanded(!isExpanded));
  };

  const handleSendMessage = useCallback(
    (request: PrivateMessageType) => {
      if (request) {
        audioTrigger.play({ name: 'menuClick' });
        if (isMobile) {
          dispatch(chatActions.setIsPrivateOptionsExpanded(false));
        }
        // Send message to chat server via websocket
        const message = {
          ...generateMessage(userId, request.message, tableId, casinoId, true),
          priveRequestStatus: 'PENDING',
          priveRequestType: request.priveRequestType,
        };
        chatCommunicationController.sendMessage(JSON.stringify(message));
      }
    },
    [audioTrigger, casinoId, dispatch, isMobile, tableId, userId]
  );

  return (
    <PrivateChatOptions
      expanded={isExpanded}
      privateChatOptions={privateChatOptions}
      handleSendMessage={handleSendMessage}
      onToggle={onToggle}
      isDisabled={isSpectator}
    />
  );
};
